import CubeIndex, {AxisItem} from '../cube/CubeIndex';
import {computeDelta, respondents} from '../render/format';
import {isLowBase} from '../render/lowBase';
import {Cube, CubeCellValue, CubeMeasure, MEASURES_CHANNEL, RenderContext, RenderSpec, STATS_KEYS} from '../types';
import {TableCell, TableColumn, TableRow, TableSpec, TableValue, tableToCsv} from './model';

interface TableLayout {
    /** Channels whose member combinations form the rows */
    rows: string[];
    /** Channel spread into columns; every measure then gets its own columns, unless a row fixes the measure */
    columns?: string;
    /** Path marks drop total rows and combinations nobody answered */
    pathOnly: boolean;
}

const PATH_MARKS = ['sankey', 'treemap', 'sunburst'];

function stringChannel(spec: RenderSpec, name: string): string | undefined {
    const value = spec.encoding[name];

    return typeof value === 'string' ? value : undefined;
}

function tableLayout(cube: Cube, spec: RenderSpec): TableLayout {
    if (spec.mark === 'diverging') {
        return {
            rows: [stringChannel(spec, 'y') ?? MEASURES_CHANNEL],
            columns: stringChannel(spec, 'series') ?? '$values',
            pathOnly: false,
        };
    }

    const series = stringChannel(spec, 'series');
    const columns = series && series !== MEASURES_CHANNEL ? series : undefined;

    return {
        rows: cube.dimensions.map(dimension => dimension.id).filter(id => id !== columns),
        columns,
        pathOnly: PATH_MARKS.includes(spec.mark),
    };
}

function rowCombinations(index: CubeIndex, channels: string[]): AxisItem[][] {
    return channels.reduce<AxisItem[][]>(
        (combinations, id) => combinations.flatMap(combination => index.items(id).map(item => [...combination, item])),
        [[]],
    );
}

const LABEL_COLUMN = 'label';

/** Cells of one (column item x measure) combination; `shape` is the measure its columns were built for. */
interface CellGroup {
    prefix: string;
    shape: CubeMeasure;
    measure: CubeMeasure;
    /** Set when the measure comes from the row, so the column has none */
    rowMeasure?: CubeMeasure;
    value?: CubeCellValue;
}

function groupColumns(
    prefix: string,
    shape: CubeMeasure,
    measure: CubeMeasure | undefined,
    label: string,
    compared: boolean,
    ctx: RenderContext,
): TableColumn[] {
    const count: TableColumn = {id: `${prefix}.n`, kind: 'count', label: 'n'};
    if (shape.metric === 'stats') {
        return [
            ...STATS_KEYS.map((stat): TableColumn => ({
                id: `${prefix}.${stat}`,
                kind: 'value',
                label: `${label} ${ctx.labels?.[stat] ?? stat}`,
                measure,
                stat,
            })),
            count,
        ];
    }

    return [
        {id: `${prefix}.value`, kind: 'value', label, measure},
        count,
        ...(compared ? [{id: `${prefix}.delta`, kind: 'delta' as const, label: 'Δ', measure}] : []),
    ];
}

function groupCells(group: CellGroup, lowBase: boolean, compared: boolean, ctx: RenderContext): Record<string, TableCell> {
    const {prefix, shape, measure, rowMeasure, value} = group;
    const cell = (cellValue: TableValue): TableCell => rowMeasure
        ? {value: cellValue, lowBase, measure: rowMeasure}
        : {value: cellValue, lowBase};
    const cells: Record<string, TableCell> = {[`${prefix}.n`]: {value: respondents(value), lowBase}};

    if (shape.metric === 'stats') {
        STATS_KEYS.forEach((stat, position) => cells[`${prefix}.${stat}`] = cell(value?.stats?.[position] ?? null));
    } else {
        cells[`${prefix}.value`] = cell(value?.value ?? null);
        if (compared) {
            cells[`${prefix}.delta`] = cell(computeDelta(value?.value, value?.previous, measure, ctx)?.value ?? null);
        }
    }

    return cells;
}

/**
 * Pivot built straight from the cube (rows/columns by `tableLayout`). A cell yields value + n, or the five-number
 * summary + n for `stats` measures, and a delta when the cube was compared with a previous term. Values stay raw.
 */
export function cubeToTable(cube: Cube, spec: RenderSpec, ctx: RenderContext): TableSpec {
    const index = new CubeIndex(cube);
    const layout = tableLayout(cube, spec);
    const columnItems = layout.columns ? index.items(layout.columns) : [undefined];
    const measuresOnRows = layout.rows.includes(MEASURES_CHANNEL);
    const compared = cube.cells.some(cell => Object.values(cell.values).some(v => v.previous !== undefined && v.previous !== null));
    // With measures on rows the header cannot name a measure, the first one only decides the column shape
    const headerMeasures = measuresOnRows ? [cube.measures[0]] : cube.measures;
    const prefix = (columnPosition: number, measurePosition: number): string => `c${columnPosition}.m${measurePosition}`;

    const columns: TableColumn[] = [{id: LABEL_COLUMN, kind: 'label', label: '', sticky: true}];
    columnItems.forEach((columnItem, columnPosition) => headerMeasures.forEach((measure, measurePosition) => {
        const parts = [columnItem?.label, columnItem && (measuresOnRows || cube.measures.length === 1) ? '' : measure.label];
        const label = parts.filter(Boolean).join(' – ') || measure.label;
        // rows carry their own measure, the column has none to format or label a unit with
        const columnMeasure = measuresOnRows ? undefined : measure;
        columns.push(...groupColumns(prefix(columnPosition, measurePosition), measure, columnMeasure, label, compared, ctx));
    }));

    const rows: TableRow[] = [];
    rowCombinations(index, layout.rows).forEach(combination => {
        const positions: Record<string, number> = {};
        layout.rows.forEach((id, position) => {
            if (id !== MEASURES_CHANNEL) {
                positions[id] = combination[position].index;
            }
        });
        const rowMeasure = combination.find(item => item.measure)?.measure;
        const groups: CellGroup[] = [];
        columnItems.forEach((columnItem, columnPosition) => (rowMeasure ? [rowMeasure] : cube.measures).forEach((measure, measurePosition) => {
            const columnPositions = layout.columns && columnItem ? {...positions, [layout.columns]: columnItem.index} : positions;
            groups.push({
                prefix: prefix(columnPosition, measurePosition),
                shape: headerMeasures[measurePosition],
                measure,
                rowMeasure,
                value: index.value(columnPositions, measure.id),
            });
        }));

        const isTotal = combination.some(item => item.member?.role === 'total');
        if (layout.pathOnly && (isTotal || groups.every(group => !group.value?.count))) {
            return;
        }

        const label: TableCell = {value: combination.map(item => item.label).join(' / ')};
        const color = combination[0]?.member?.color;
        if (color) {
            label.color = color;
        }
        const cells: Record<string, TableCell> = {[LABEL_COLUMN]: label};
        groups.forEach(group => Object.assign(cells, groupCells(group, isLowBase(cube, group.value), compared, ctx)));

        rows.push({key: JSON.stringify(combination.map(item => item.id)), role: isTotal ? 'total' : 'data', cells});
    });

    return {columns, rows};
}

export function cubeToCsv(cube: Cube, spec: RenderSpec, ctx: RenderContext): TableValue[][] {
    return tableToCsv(cubeToTable(cube, spec, ctx));
}
